import apiClient from './api.service';

export type MrmStatus =
  | 'Draft'
  | 'Pending Agenda Approval'
  | 'Agenda Approved'
  | 'Minutes In Progress'
  | 'Pending Minutes Approval'
  | 'Closed';

export interface MrmParticipant {
  id?: string;
  participant_role?: 'required' | 'attendee' | string;
  employee_id?: string | null;
  name: string;
  designation?: string | null;
  attended?: boolean | number | null;
  signature_note?: string | null;
  sort_order?: number;
}

export interface MrmTopic {
  id: string;
  topic_code: string;
  title: string;
  sort_order: number;
  included_on_agenda?: boolean | number;
  overview_comments?: string | null;
  action_planned?: string | null;
  action_target_date?: string | null;
  action_responsibility?: string | null;
  additional_remarks?: string | null;
}

export interface MrmAction {
  id?: string;
  source_topic_id?: string | null;
  carried_from_action_id?: string | null;
  description: string;
  owner_employee_id?: string | null;
  owner_name?: string | null;
  due_date?: string | null;
  status?: string;
  effectiveness_notes?: string | null;
  capa_id?: string | null;
  sort_order?: number;
}

export interface ManagementReviewMeeting {
  id: string;
  company_id: string;
  mrm_number: string;
  status: MrmStatus | string;
  department_id?: string | null;
  previous_mrm_id?: string | null;
  proposed_date?: string | null;
  proposed_time?: string | null;
  venue?: string | null;
  meeting_date?: string | null;
  place_of_meeting?: string | null;
  chaired_by_employee_id?: string | null;
  chaired_by_name?: string | null;
  chaired_by_designation?: string | null;
  next_mrm_date?: string | null;
  qms_effectiveness_summary?: string | null;
  agenda_prepared_by_name?: string | null;
  agenda_prepared_by_designation?: string | null;
  agenda_prepared_date?: string | null;
  minutes_updated_by_name?: string | null;
  minutes_updated_by_designation?: string | null;
  minutes_updated_date?: string | null;
  agenda_approval_instance_id?: string | null;
  minutes_approval_instance_id?: string | null;
  notes?: string | null;
  open_action_count?: number;
  overdue_next?: number;
  participants?: MrmParticipant[];
  topics?: MrmTopic[];
  actions?: MrmAction[];
  previous_mrm?: ManagementReviewMeeting | null;
}

function unwrap<T>(response: any): T {
  const raw = response?.data ?? response;
  return (raw?.data ?? raw) as T;
}

const managementReviewService = {
  async list(filters?: Record<string, unknown>): Promise<ManagementReviewMeeting[]> {
    const response = await apiClient.get('/quality/management-reviews', { params: filters });
    const data = unwrap<any>(response);
    return Array.isArray(data) ? data : [];
  },

  async get(id: string): Promise<ManagementReviewMeeting> {
    const response = await apiClient.get(`/quality/management-reviews/${id}`);
    return unwrap(response);
  },

  async create(body: Partial<ManagementReviewMeeting> & { participants?: MrmParticipant[] }) {
    const response = await apiClient.post('/quality/management-reviews', body);
    return unwrap<ManagementReviewMeeting>(response);
  },

  async update(id: string, body: Record<string, unknown>) {
    const response = await apiClient.put(`/quality/management-reviews/${id}`, body);
    return unwrap<ManagementReviewMeeting>(response);
  },

  async remove(id: string) {
    await apiClient.delete(`/quality/management-reviews/${id}`);
  },

  async submitAgenda(id: string) {
    const response = await apiClient.post(`/quality/management-reviews/${id}/submit-agenda`);
    return unwrap<ManagementReviewMeeting>(response);
  },

  async startMinutes(id: string) {
    const response = await apiClient.post(`/quality/management-reviews/${id}/start-minutes`);
    return unwrap<ManagementReviewMeeting>(response);
  },

  async submitMinutes(id: string) {
    const response = await apiClient.post(`/quality/management-reviews/${id}/submit-minutes`);
    return unwrap<ManagementReviewMeeting>(response);
  },

  async createCapaFromAction(
    id: string,
    actionId: string,
    body: {
      responsible_person_id: string;
      description?: string;
      action_plan?: string;
      target_completion_date?: string;
      department_id?: string | null;
    }
  ) {
    const response = await apiClient.post(
      `/quality/management-reviews/${id}/actions/${actionId}/create-capa`,
      body
    );
    return unwrap<ManagementReviewMeeting>(response);
  },

  async dashboardStats(): Promise<{ open_actions: number; overdue_next_mrm: number; in_flight: number }> {
    const response = await apiClient.get('/quality/management-reviews/dashboard-stats');
    return unwrap(response);
  },

  async relatedSummary(): Promise<Record<string, number>> {
    const response = await apiClient.get('/quality/management-reviews/related-summary');
    return unwrap(response);
  },
};

export default managementReviewService;
